import { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Shell header slot — lets a page project content into the shell's
 * top bar instead of stacking its own chrome band below it.
 *
 * Wiring:
 *   - RootLayout wraps the tree in <ShellSlotProvider>.
 *   - Header renders <ShellHeaderSlot /> where projected content
 *     should appear (the flexible middle of the h-14 bar).
 *   - A page renders <ShellHeader>…</ShellHeader>; its children are
 *     portalled into the slot for as long as the page is mounted.
 *
 * The slot element lives in React state (not a ref) so consumers
 * re-render and portal in once the slot mounts.
 */

interface ShellSlots {
    headerSlot: HTMLElement | null;
    setHeaderSlot: (el: HTMLElement | null) => void;
}

const ShellSlotContext = createContext<ShellSlots | null>(null);

export function ShellSlotProvider({ children }: { children: React.ReactNode }) {
    const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
    return (
        <ShellSlotContext.Provider value={{ headerSlot, setHeaderSlot }}>
            {children}
        </ShellSlotContext.Provider>
    );
}

/** Portal target rendered by the shell's Header. */
export function ShellHeaderSlot() {
    const slots = useContext(ShellSlotContext);
    return (
        <div
            ref={slots ? slots.setHeaderSlot : undefined}
            className="flex min-w-0 flex-1 items-center gap-3"
        />
    );
}

/**
 * Projects children into the shell header. Renders nothing when used
 * outside a ShellSlotProvider (e.g. a layout without a header), so
 * pages don't need to know which shell hosts them.
 */
export function ShellHeader({ children }: { children: React.ReactNode }) {
    const slots = useContext(ShellSlotContext);
    if (!slots?.headerSlot) return null;
    return createPortal(children, slots.headerSlot);
}
